// frontend/src/contexts/PlatformContext.js
import React, { createContext, useContext, useEffect, useMemo, useState } from 'react';

const PlatformContext = createContext(null);

export const usePlatform = () => {
  const ctx = useContext(PlatformContext);
  if (!ctx) throw new Error('usePlatform must be used within PlatformProvider');
  return ctx;
};

const safeStr = (v) => String(v || '').trim();

const readUserSite = () => {
  try {
    const u = JSON.parse(localStorage.getItem('userData') || 'null');
    const s = u?.site;
    return s && typeof s === 'object' ? s : null;
  } catch {
    return null;
  }
};

/**
 * platform shape:
 * - Admin:     { type: 'ADMIN' }
 * - Directors: { type: 'DIRECTORS' }
 * - Site:      { type: 'SITE', siteId, siteCode, siteName }
 */
export const PlatformProvider = ({ children }) => {
  const [platform, setPlatformState] = useState(() => {
    try {
      return JSON.parse(localStorage.getItem('platform') || 'null');
    } catch {
      return null;
    }
  });

  /**
   * Keep platform state + localStorage in sync
   */
  const setPlatform = (p) => {
    setPlatformState(p || null);
    try {
      if (p) localStorage.setItem('platform', JSON.stringify(p));
      else localStorage.removeItem('platform');
    } catch {}
  };

  /**
   * Clear platform + clear related site selection storage
   */
  const clearPlatform = () => {
    setPlatformState(null);
    try {
      localStorage.removeItem('platform');

      // also clear platform-specific site memory
      localStorage.removeItem('selectedSiteId');
      localStorage.removeItem('selectedSiteCode');
      localStorage.removeItem('selectedSiteName');
    } catch {}
  };

  /**
   * ✅ Auto-hydrate site platform to ensure siteName/siteCode are present,
   * so UI never falls back to showing a Mongo ObjectId.
   */
  useEffect(() => {
    if (!platform) return;

    if (platform.type !== 'SITE') return;

    const p = platform || {};
    const siteObj = readUserSite();

    const siteId =
      safeStr(p.siteId) ||
      safeStr(localStorage.getItem('selectedSiteId')) ||
      safeStr(siteObj?._id);

    const siteCode =
      safeStr(p.siteCode) ||
      safeStr(localStorage.getItem('selectedSiteCode')) ||
      safeStr(siteObj?.code);

    const siteName =
      safeStr(p.siteName) ||
      safeStr(localStorage.getItem('selectedSiteName')) ||
      safeStr(siteObj?.name);

    const next = { ...p, type: 'SITE', siteId, siteCode, siteName };

    // only write if something changed
    const changed =
      next.siteId !== p.siteId ||
      next.siteCode !== p.siteCode ||
      next.siteName !== p.siteName;

    if (changed) setPlatform(next);
  }, [platform?.type]);

  /**
   * Convenience getters:
   * - Use siteId ONLY for API calls
   * - Use siteName/siteCode for display + routing
   */
  const siteId = useMemo(() => {
    if (platform?.type !== 'SITE') return '';
    return safeStr(platform?.siteId || localStorage.getItem('selectedSiteId'));
  }, [platform]);

  const siteCode = useMemo(() => {
    if (platform?.type !== 'SITE') return '';
    return safeStr(platform?.siteCode || localStorage.getItem('selectedSiteCode'));
  }, [platform]);

  const siteName = useMemo(() => {
    if (platform?.type !== 'SITE') return '';
    return safeStr(platform?.siteName || localStorage.getItem('selectedSiteName'));
  }, [platform]);

  const value = useMemo(
    () => ({
      platform,
      setPlatform,
      clearPlatform,

      // ✅ helpers
      siteId,
      siteCode,
      siteName,
    }),
    [platform, siteId, siteCode, siteName]
  );

  return <PlatformContext.Provider value={value}>{children}</PlatformContext.Provider>;
};
